import { CMSSection } from "@/cms/models";
import {
  getDesktopHeight,
  getMargins,
  getMobileHeight,
  getPaddings,
  getSectionClassName,
  ownSectionHeights,
} from "@/utils/theme-helper";
import React from "react";
import SectionBackground from "./SectionBackground";
import { responsiveVariables } from "@/utils/visual-style";
import { LookSystemProvider } from "@/components/motion/LookMotion";
import { boxCss, type BoxSides } from "@/purpose-sections/length";

const CLASS_TOKEN = /^(?:none|xs|sm|md|lg|xl|2xl)$/;
const sides = (box: BoxSides | undefined) =>
  box ? `${box.top} ${box.right} ${box.bottom} ${box.left}` : "0px";

/**
 * With explicit spacing a Luft indeni or Luft udenom holding a CSS length or a Tæthed step, which no class draws, is
 * drawn from `--re-own-<name>[-desktop]` as layered sections read it (boxCss); one of tokens only keeps its classes.
 */
function ownBox(
  raw: unknown,
  name: "padding" | "margin",
  explicit: boolean,
): Record<string, string> | undefined {
  if (!explicit || typeof raw !== "string") return undefined;
  const tokens = raw.split(/[\s|]+/).filter(Boolean);
  if (tokens.every((token) => CLASS_TOKEN.test(token))) return undefined;
  const box = boxCss(raw);
  return {
    [`--re-own-${name}`]: sides(box.mobile),
    [`--re-own-${name}-desktop`]: sides(box.desktop),
  };
}

const SectionLayout: React.FC<{
  children: React.ReactNode;
  cmsSection: CMSSection<any>;
  /** Its elements reveal one by one on the look system (elementUnit in utils/motion.ts), so its content does not. */
  revealsByElement?: boolean;
  /** Its content is as wide as Afstand og bredde's Indholdsbredde instead of its width class. */
  followsContentWidth?: boolean;
  /**
   * How Højde (ui.height) and Fyld skærmhøjden (ui.viewport) size a property-page section; without it Højde is a fixed
   * height the content may spill out of. Either value also keeps the content one full-width column where Fyld
   * skærmhøjden or Farveforløb og billede på mobil (ui.background) would lay it out as a row.
   * - `fill`: Højde, or the screen's height with Fyld skærmhøjden, is the least the section is high, and the content is
   *   stretched to it less the padding; content that needs more makes the section taller.
   * - `content`: the section is as tall as its content, whatever the two settings store.
   */
  ownHeight?: "fill" | "content";
}> = ({
  children,
  cmsSection,
  revealsByElement = false,
  followsContentWidth = false,
  ownHeight,
}) => {
  const getSizeClassName = () => {
    switch (cmsSection.ui?.width) {
      case "sm":
        return "max-w-screen-sm";
      case "md":
        return "max-w-screen-md";
      case "lg":
        return "max-w-screen-lg";
      case "xl":
        return "max-w-screen-xl";
      case "2xl":
        return "max-w-screen-2xl";
      case "4xl":
        return "max-w-screen-4xl";
      case "full":
        return "w-full";
      default:
        return "max-w-screen-xl";
    }
  };

  const ui =
    ownHeight === "content"
      ? { ...cmsSection.ui, height: undefined, viewport: undefined }
      : cmsSection.ui;
  const bgImage = cmsSection.specifics?.bgImage ?? ui?.bgImage ?? "";
  const enhanced = !!(ui.background || ui.viewport || ui.minHeight);
  const sectionClass = getSectionClassName(cmsSection.id);
  const look = cmsSection.lookAttributes;
  const fills = ownHeight === "fill" ? ownSectionHeights(ui) : "none";
  const explicit = !!cmsSection.explicitSpacing;
  const ownPadding = ownBox(ui.padding, "padding", explicit);
  const ownMargin = ownBox(ui.margin, "margin", explicit);
  const contentHeight =
    fills === "none"
      ? ownHeight && enhanced
        ? " flex-col"
        : ""
      : ui.viewport
        ? " min-h-[100svh] grid grid-cols-1"
        : " min-h-[var(--section-mobile-height)] md:min-h-[var(--section-desktop-height)] grid grid-cols-1";

  return (
    <section
      data-section-type={cmsSection?.type}
      data-section-role={cmsSection.role}
      data-re-bg={look?.["data-re-bg"]}
      data-re-framed={
        look?.frame ? (cmsSection.explicitFrame ? "explicit" : "") : undefined
      }
      {...look?.motion}
      className={`flex flex-1 ${sectionClass} ${ownMargin ? "m-[var(--re-own-margin)] md:m-[var(--re-own-margin-desktop)]" : getMargins(ui.margin)}`}
      style={{
        ...ownMargin,
        backgroundImage:
          !ui.background && bgImage ? `url('${bgImage}')` : "none",
        backgroundSize: ui.bgSize ?? "cover",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: ui.backgroundPositionX ?? "center",
        backgroundPositionY: ui.backgroundPositionY ?? "center",
        ...look?.style,
        ...look?.frame,
      }}
    >
      <style>
        {` ${`.${sectionClass}`} {
        --section-text-color: ${cmsSection.specifics?.textColor ?? ui?.textColor ?? cmsSection.specifics?.farvetekst ?? "inherit"};
        --section-bg-color: ${look?.background ?? cmsSection.specifics?.bgColor ?? ui?.bgColor ?? cmsSection.specifics?.farvebaggrund ?? "transparent"};
        --section-desktop-height: ${getDesktopHeight(ui?.height)};
        --section-mobile-height: ${getMobileHeight(ui?.height)};
        }`}
      </style>
      <div
        style={
          enhanced
            ? responsiveVariables("section-min-height", ui.minHeight, "0px")
            : undefined
        }
        className={`flex flex-1 justify-center ${enhanced ? "re-section" : ""} ${ui.viewport ? "re-section-viewport" : fills !== "none" ? "" : "h-[var(--section-mobile-height)] md:h-[var(--section-desktop-height)]"} bg-[var(--section-bg-color)] w-full text-[var(--section-text-color)] relative`}
      >
        {ui.background && (
          <SectionBackground image={bgImage} background={ui.background} />
        )}
        <div
          className={`${enhanced ? "re-section-content" : ""} ${followsContentWidth ? "" : getSizeClassName()} w-full flex-1 ${ownPadding ? "p-[var(--re-own-padding)] md:p-[var(--re-own-padding-desktop)]" : getPaddings(ui?.padding)}${contentHeight}`}
          style={
            followsContentWidth || ownPadding
              ? {
                  ...ownPadding,
                  ...(followsContentWidth
                    ? { maxWidth: "var(--re-content-width, 1280px)" }
                    : {}),
                }
              : undefined
          }
          data-re-reveal={look?.motion && !revealsByElement ? "" : undefined}
        >
          {look?.motion ? (
            <LookSystemProvider motion={look.motion}>
              {children}
            </LookSystemProvider>
          ) : (
            children
          )}
        </div>
      </div>
    </section>
  );
};

export default SectionLayout;
